import { useState, useEffect } from 'react';
import { useEmpresa } from '@/contexts/EmpresaContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useGrupoEmpresa } from '@/contexts/GrupoEmpresaContext';
import { empresasClientes } from '@/mocks/admin/empresasClientes';
import { getGrupoById } from '@/mocks/admin/gruposEmpresas';

export default function GrupoEmpresaSection() {
  const { empresaSelecionada, empresaPertenceAGrupo, grupoEmpresaIds } = useEmpresa();
  const { empresaId } = useAdminAuth();
  const {
    grupoAtual,
    convitesPendentes,
    convitesEnviados,
    carregarDados,
    criarNovoGrupo,
    editarGrupoAtual,
    convidarEmpresa,
    aceitarConvite,
    rejeitarConvite,
    cancelarConviteEnviado,
    removerMembro,
    desfazerGrupo,
    empresasForaDoGrupo,
  } = useGrupoEmpresa();

  const [showCriarGrupo, setShowCriarGrupo] = useState(false);
  const [showEditarGrupo, setShowEditarGrupo] = useState(false);
  const [showConvidar, setShowConvidar] = useState(false);
  const [showDissolver, setShowDissolver] = useState(false);
  const [nomeGrupo, setNomeGrupo] = useState('');
  const [descricaoGrupo, setDescricaoGrupo] = useState('');
  const [cnpjBusca, setCnpjBusca] = useState('');
  const [empresaEncontrada, setEmpresaEncontrada] = useState<typeof empresasClientes[0] | null>(null);
  const [buscandoEmpresa, setBuscandoEmpresa] = useState(false);
  const [buscaErro, setBuscaErro] = useState('');
  const [erroForm, setErroForm] = useState('');
  const [toastMsg, setToastMsg] = useState('');

  const empresaBackofficeId = empresaSelecionada.empresaBackofficeId || '';

  // Carregar dados do grupo quando montar
  useEffect(() => {
    if (empresaBackofficeId) {
      carregarDados(empresaBackofficeId);
    }
  }, [empresaBackofficeId, carregarDados]);

  const mostrarToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const handleCriarGrupo = () => {
    if (!nomeGrupo.trim()) {
      setErroForm('Nome do grupo é obrigatório');
      return;
    }
    criarNovoGrupo(nomeGrupo.trim(), descricaoGrupo.trim(), empresaBackofficeId);
    mostrarToast(`Grupo "${nomeGrupo.trim()}" criado com sucesso!`);
    setShowCriarGrupo(false);
    setNomeGrupo('');
    setDescricaoGrupo('');
    setErroForm('');
  };

  const handleEditarGrupo = () => {
    if (!nomeGrupo.trim()) {
      setErroForm('Nome do grupo é obrigatório');
      return;
    }
    editarGrupoAtual(nomeGrupo.trim(), descricaoGrupo.trim());
    mostrarToast(`Grupo "${nomeGrupo.trim()}" atualizado com sucesso!`);
    setShowEditarGrupo(false);
    setErroForm('');
  };

  const handleAbrirEdicao = () => {
    if (!grupoAtual) return;
    setNomeGrupo(grupoAtual.nome);
    setDescricaoGrupo(grupoAtual.descricao);
    setErroForm('');
    setShowEditarGrupo(true);
  };

  const handleAbrirConvidar = () => {
    setCnpjBusca('');
    setEmpresaEncontrada(null);
    setBuscaErro('');
    setBuscandoEmpresa(false);
    setShowConvidar(true);
  };

  const handleBuscarPorCnpj = () => {
    setBuscaErro('');
    setEmpresaEncontrada(null);
    const cnpjLimpo = cnpjBusca.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setBuscaErro('CNPJ inválido. Digite 14 números.');
      return;
    }
    setBuscandoEmpresa(true);
    const cnpjFormatado = cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    const encontrada = empresasClientes.find(e => e.cnpj === cnpjFormatado);
    if (encontrada) {
      if (grupoEmpresaIds.includes(encontrada.id)) {
        setBuscaErro('Esta empresa já faz parte do grupo ou possui um grupo.');
      } else {
        setEmpresaEncontrada(encontrada);
      }
    } else {
      setBuscaErro('Empresa não encontrada na base de dados.');
    }
    setBuscandoEmpresa(false);
  };

  const handleConvidar = () => {
    if (!empresaEncontrada || !grupoAtual) return;
    const empresaNome = empresaSelecionada.nome;
    convidarEmpresa(grupoAtual.id, empresaBackofficeId, empresaNome, empresaEncontrada.id);
    mostrarToast(`Convite enviado para ${empresaEncontrada.nome}!`);
    setShowConvidar(false);
    setCnpjBusca('');
    setEmpresaEncontrada(null);
  };

  const handleAceitarConvite = (conviteId: string) => {
    const result = aceitarConvite(conviteId);
    if (result) {
      mostrarToast('Convite aceito! Empresa adicionada ao grupo.');
    }
  };

  const handleRejeitarConvite = (conviteId: string) => {
    const result = rejeitarConvite(conviteId);
    if (result) {
      mostrarToast('Convite rejeitado.');
    }
  };

  const handleCancelarConvite = (conviteId: string) => {
    const result = cancelarConviteEnviado(conviteId);
    if (result) {
      mostrarToast('Convite cancelado.');
    }
  };

  const handleRemoverMembro = (empId: string) => {
    if (!grupoAtual) return;
    const empresa = empresasClientes.find(e => e.id === empId);
    if (removerMembro(grupoAtual.id, empId)) {
      mostrarToast(`${empresa?.nome || empId} removido(a) do grupo.`);
    }
  };

  const handleDissolver = () => {
    if (!grupoAtual) return;
    desfazerGrupo(grupoAtual.id);
    mostrarToast('Grupo dissolvido.');
    setShowDissolver(false);
  };

  const getEmpresaNome = (empId: string) => {
    const e = empresasClientes.find(ec => ec.id === empId);
    if (e) return e.nome;
    if (empId === 'EMP-001') return 'getPoints';
    if (empId === 'EMP-002') return 'Inovação Digital S.A.';
    if (empId === 'EMP-003') return 'Consultoria Tech Brasil';
    if (empId === 'EMP-004') return 'Sistemas Integrados Ltda';
    return empId;
  };

  const isCriador = grupoAtual?.criadoPor === empresaBackofficeId;
  
  // Convites pendentes enviados pelo grupo atual
  const convitesEnviadosPendentes = convitesEnviados.filter(c => c.status === 'pendente');

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-group-line text-orange-500"></i>
          </div>
          <span>Grupos de Empresas</span>
        </h3>
        {!grupoAtual && (
          <button
            onClick={() => setShowCriarGrupo(true)}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            <span>Criar Grupo</span>
          </button>
        )}
      </div>

      {/* Sem grupo */}
      {!grupoAtual && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-group-line text-gray-400 text-xl"></i>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">Esta empresa não faz parte de nenhum grupo</p>
          <p className="text-xs text-gray-400 mb-4">Crie um grupo para compartilhar gestão entre empresas relacionadas</p>
          <button
            onClick={() => setShowCriarGrupo(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Criar Grupo
          </button>
        </div>
      )}

      {/* Com grupo */}
      {grupoAtual && (
        <div className="space-y-5">
          {/* Info do grupo */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-group-fill text-white text-xl"></i>
                  </div>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-base font-bold text-gray-900">{grupoAtual.nome}</h4>
                    {isCriador && (
                      <button
                        onClick={handleAbrirEdicao}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-orange-100 rounded-md cursor-pointer transition-colors"
                        title="Editar grupo"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    )}
                  </div>
                  {grupoAtual.descricao && (
                    <p className="text-xs text-gray-500 mt-0.5">{grupoAtual.descricao}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Criado em {new Date(grupoAtual.dataCriacao).toLocaleDateString('pt-BR')} • {grupoAtual.membros.length} membros
                  </p>
                </div>
              </div>
              {isCriador && (
                <button
                  onClick={() => setShowDissolver(true)}
                  className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  Dissolver Grupo
                </button>
              )}
            </div>
          </div>

          {/* Membros */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-900 flex items-center space-x-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-team-line text-gray-500"></i>
                </div>
                <span>Empresas do Grupo</span>
              </h4>
              {isCriador && (
                <button
                  onClick={handleAbrirConvidar}
                  className="flex items-center space-x-1 px-2.5 py-1 text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-user-add-line"></i>
                  </div>
                  <span>Convidar</span>
                </button>
              )}
            </div>
            <div className="space-y-2">
              {grupoAtual.membros.map((membroId) => {
                const nomeMembro = getEmpresaNome(membroId);
                const isCurrent = membroId === empresaBackofficeId;
                const isCreator = membroId === grupoAtual.criadoPor;
                return (
                  <div
                    key={membroId}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCurrent ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isCurrent ? 'bg-orange-200' : 'bg-gray-100'
                      }`}>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-building-4-line ${isCurrent ? 'text-orange-600' : 'text-gray-500'} text-sm`}></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{nomeMembro}</p>
                        <p className="text-xs text-gray-500">{membroId}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isCreator && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                          Criador
                        </span>
                      )}
                      {isCurrent && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                          Você
                        </span>
                      )}
                      {isCriador && !isCreator && (
                        <button
                          onClick={() => handleRemoverMembro(membroId)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
                          title="Remover empresa do grupo"
                        >
                          <i className="ri-close-circle-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Convites enviados (apenas criador) */}
          {isCriador && convitesEnviadosPendentes.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-send-plane-line text-blue-500"></i>
                </div>
                <span>Convites Enviados</span>
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">
                  {convitesEnviadosPendentes.length}
                </span>
              </h4>
              <div className="space-y-2">
                {convitesEnviadosPendentes.map((convite) => (
                  <div key={convite.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-blue-200 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-user-received-line text-blue-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">
                          {getEmpresaNome(convite.paraEmpresaId)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Enviado em {new Date(convite.dataEnvio).toLocaleDateString('pt-BR')} • Aguardando resposta
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelarConvite(convite.id)}
                      className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Convites pendentes recebidos */}
          {convitesPendentes.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-unread-line text-amber-500"></i>
                </div>
                <span>Convites Pendentes Recebidos</span>
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full">
                  {convitesPendentes.length}
                </span>
              </h4>
              <div className="space-y-2">
                {convitesPendentes.map((convite) => (
                  <div key={convite.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-amber-200 rounded-lg flex items-center justify-center">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-mail-line text-amber-600 text-sm"></i>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">
                          {convite.deEmpresaNome} convidou para "{convite.grupoNome}"
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(convite.dataEnvio).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleAceitarConvite(convite.id)}
                        className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                      >
                        Aceitar
                      </button>
                      <button
                        onClick={() => handleRejeitarConvite(convite.id)}
                        className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                      >
                        Rejeitar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alertas */}
          {!isCriador && grupoAtual && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-blue-500"></i>
              </div>
              <p className="text-xs text-blue-700">
                Apenas o criador do grupo pode editar, convidar novas empresas ou remover membros.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal Criar Grupo */}
      {showCriarGrupo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Criar Grupo de Empresas</h3>
              <button
                onClick={() => { setShowCriarGrupo(false); setErroForm(''); }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 block">Nome do Grupo *</label>
                <input
                  type="text"
                  value={nomeGrupo}
                  onChange={(e) => { setNomeGrupo(e.target.value); setErroForm(''); }}
                  placeholder="Ex: Grupo Financeiro"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 block">Descrição</label>
                <input
                  type="text"
                  value={descricaoGrupo}
                  onChange={(e) => setDescricaoGrupo(e.target.value)}
                  placeholder="Descreva o objetivo do grupo"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                />
              </div>
              {erroForm && (
                <p className="text-xs text-red-500 flex items-center space-x-1">
                  <i className="ri-error-warning-line"></i>
                  <span>{erroForm}</span>
                </p>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <i className="ri-information-line mr-1"></i>
                  Sua empresa será a criadora do grupo. Você poderá convidar outras empresas depois.
                </p>
              </div>
              <div className="flex space-x-3 pt-1">
                <button
                  onClick={() => { setShowCriarGrupo(false); setErroForm(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCriarGrupo}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Criar Grupo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Grupo */}
      {showEditarGrupo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Editar Grupo</h3>
              <button
                onClick={() => { setShowEditarGrupo(false); setErroForm(''); }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 block">Nome do Grupo *</label>
                <input
                  type="text"
                  value={nomeGrupo}
                  onChange={(e) => { setNomeGrupo(e.target.value); setErroForm(''); }}
                  placeholder="Ex: Grupo Financeiro"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 block">Descrição</label>
                <input
                  type="text"
                  value={descricaoGrupo}
                  onChange={(e) => setDescricaoGrupo(e.target.value)}
                  placeholder="Descreva o objetivo do grupo"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                />
              </div>
              {erroForm && (
                <p className="text-xs text-red-500 flex items-center space-x-1">
                  <i className="ri-error-warning-line"></i>
                  <span>{erroForm}</span>
                </p>
              )}
              <div className="flex space-x-3 pt-1">
                <button
                  onClick={() => { setShowEditarGrupo(false); setErroForm(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEditarGrupo}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Convidar Empresa */}
      {showConvidar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Convidar Empresa</h3>
              <button
                onClick={() => { setShowConvidar(false); setCnpjBusca(''); setEmpresaEncontrada(null); setBuscaErro(''); }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Digite o CNPJ da empresa para convidar ao grupo <strong>{grupoAtual?.nome}</strong>.
              </p>
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 block">CNPJ da Empresa</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={cnpjBusca}
                    onChange={(e) => { setCnpjBusca(e.target.value); setEmpresaEncontrada(null); setBuscaErro(''); }}
                    placeholder="00.000.000/0000-00"
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                  />
                  <button
                    onClick={handleBuscarPorCnpj}
                    disabled={buscandoEmpresa}
                    className="px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {buscandoEmpresa ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
              </div>
              {buscaErro && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-red-500"></i>
                  </div>
                  <p className="text-xs text-red-700">{buscaErro}</p>
                </div>
              )}
              {empresaEncontrada && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-500"></i>
                    </div>
                    <p className="text-sm font-bold text-emerald-700">Empresa encontrada</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-900">{empresaEncontrada.nome}</p>
                    <p className="text-xs text-gray-500">CNPJ: {empresaEncontrada.cnpj}</p>
                    <p className="text-xs text-gray-500">ID: {empresaEncontrada.id}</p>
                  </div>
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  <i className="ri-information-line mr-1"></i>
                  A empresa convidada receberá uma notificação e precisará aprovar o convite.
                </p>
              </div>
              <div className="flex space-x-3 pt-1">
                <button
                  onClick={() => { setShowConvidar(false); setCnpjBusca(''); setEmpresaEncontrada(null); setBuscaErro(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConvidar}
                  disabled={!empresaEncontrada}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enviar Convite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dissolver Grupo */}
      {showDissolver && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Dissolver Grupo</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tem certeza que deseja dissolver o grupo <strong>{grupoAtual?.nome}</strong>? Todos os membros serão removidos.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDissolver(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDissolver}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Dissolver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 bg-emerald-600 text-white animate-[slideUp_0.3s_ease-out]">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line text-sm"></i>
          </div>
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
}